export const DocumentTemplateBlockDocumentationMarkdown = `
# Documentation

## What does this block do?

The Generate Document Block takes data from your flow and renders it onto a pre-created Document Template. The output is a generated document (always a PDF) that incorporates the provided data into designated areas as defined in the template.

The output of the block will include a URL to the document that you can click to easily view the contents.

If the configuration is incorrect—such as an invalid UUID for the template—the block will display an error.

## When to use it?

When you have data in the flow that you want to insert on a pre-designed document.

For generating finalized versions of documents, such as Notices of Admissions (NOA), to be sent via fax or other means.

To automate the creation of documents that require dynamic data, reducing manual effort.

## Example flows

- **Flow Example 1:** A workflow that gathers patient admission details and uses the Generate Document Block to produce a Notice of Admissions (NOA) document, which is then sent via an eFax block.
- **Flow Example 2:** A workflow that collects data from various upstream blocks (e.g., patient info, appointment details) and renders a customized summary report via a Document Template.
- **Flow Example 3:** A workflow that generates or acquires multiple PDF documents that it wishes to combine into a single packet for faxing or uploading to an external system.

## How to configure this block

### Document Template

If you haven't already created your Document Template, do so in Flow Studio at:  
[https://app.notablehealth.com/staff/app#/previsit-form-templates](https://app.notablehealth.com/staff/app#/previsit-form-templates)

In the "Document Template" section, use the dropdown search field to locate and select the Document Template you created.

If the selected template has an invalid UUID (e.g., "Invalid uuid" is displayed), correct the template reference to ensure proper document rendering.

**Data Sources**

For each "Data Source" defined in your Document Template, there will be corresponding fields in the block configuration.

Fill out only those fields that correspond to the data elements you intend to render onto the template.

**Mock output for test run**

Enable the "Mock output for test run" toggle if you want to simulate the document generation process without executing a live run.

When enabled, the block will skip document rendering and return preset output data for testing purposes.

**How to test?**

1. Create a Document Template in Flow Studio at:  
   [https://app.notablehealth.com/staff/app#/previsit-form-templates](https://app.notablehealth.com/staff/app#/previsit-form-templates)
2. Add your "Text Overlays", specifying the "Data Source" and rendering options.
3. Configure the Generate Document block by selecting your Document Template from the dropdown.
4. Fill in the fields for each "Data Source" used in the Document Template.
5. Optionally, enable the "Mock output for test run" toggle to test the block without initiating a live document generation.
6. Run the flow and click into the document URL in the block output to ensure that the rendered document contains the correct data.
7. Check for any error messages (such as "Invalid uuid") and adjust the configuration accordingly.

**Best practices**

- Always ensure that the selected Document Template has a valid UUID.
- Fill out only the Data Source fields that are required by the Template to avoid unnecessary errors.
- Use the mock test mode to verify configurations and output format before running live data.
- Document how each field in the block configuration maps to the Document Template, so troubleshooting and future audits are easier.

**Example output**

~~~json
{
  "renderResponse": {
    "document": {
      "id": "b746b438-071a-4018-b7ae-214740e51205",
      "gsPath": "documents/b8355cb8-1933-4d7d-9e97-8dd0ea285af1_1473859665716.pdf",
      "created": "2024-12-21T05:14:06.751Z",
      "deleted": null,
      "updated": "2024-12-21T05:14:06.751Z",
      "documentTemplateId": "185de6bb-2898-4d78-931c-5e505ab81a84"
    }
  }
}
~~~

### Markdown

Pass in a markdown string to be rendered into a PDF document.

If your markdown is simple and static, you can declare it directly in the
"Markdown" field.

You can use data from upstream blocks by using the {{myBlock.data}} syntax. For
example, if you have a block with ID "myTextBlock" that outputs text data, you
can reference it in the markdown like this:

~~~markdown
# My Document
This document was generated using data from the flow.

Here is some dynamic content:
{{myTextBlock.data}}
~~~

For more complex markdown, you can construct a markdown string in an upstream
"Script" block and pass it to the "Markdown" field of this block.

**Limitations and considerations**

- The markdown rendering supports basic markdown syntax and a small number of
advanced features like tables. 
  - NOTE: tables can render strangely across page breaks. Use with caution.
- Currently, we do not support embedded images or advanced HTML/CSS styling.
- Because markdown is primarily designed for web content, it does not have a
concept of pages like documents. To support forced page breaks, we treat
thematic breaks (---) as page breaks in the generated PDF.

**Best practices**

- Always test your markdown generation to ensure you are using supported
markdown features. Many flavors of markdown exist, and not all are fully
supported, and our support changes over time as we add new capabilities.
- Because the templating within block fields is limited, only use very simple
references to upstream block data when declaring inline.

**Example output**

~~~json
{
  "fileReference": "fileReferenceStringHere"
}
~~~

### Combine PDF

Use this mode to combine multiple existing PDF documents into a single PDF document.

In the "File References" field, provide a JSON array of file references to the
PDF documents you want to combine. You can declare it inline or
via a script block.

#### Inline
Generate Document "File References" field:
~~~json
[
  "{{blockOutputDoc1.data.fileReference}}",
  "{{blockOutputDoc2.data.fileReference}}"
]
~~~

#### Script block

Script block body:
~~~javascript
return [
  blockOutputDoc1.data.fileReference,
  blockOutputDoc2.data.fileReference
];
~~~

Generate Document "File References" field:
~~~text
{{scriptBlock.data}}
~~~

**Limitations and considerations**

- All input files must be valid PDFs. If any file is not a valid PDF, the block
will return an error.
  - NOTE: beware accidentally passing image files
- Passing a google storage path (gs://bucket/path/to/file.pdf) is not supported.
You must use a file reference.
- The order of the file references in the array determines the order of the
document pages in the combined PDF.

**Best practices**

- PDF generation can be slow. Consider the performance implications of the flow
if you are combining many documents or documents of large size.

**Example output**

~~~json
{
  "fileReference": "fileReferenceStringHere"
}
~~~
`;
